import { OktaAuth } from "@okta/okta-auth-js";

import { AuthEndpoints } from "@generated/auth";
import { oktaConfig } from "src/domains/auth/configs/oktaConfig";
import { orgSessionManager } from "src/domains/auth/services/orgSessionManager";
import { apiBaseUrls } from "src/platform/constants/shared-constants";
import { StorageKeys } from "src/platform/constants/Storage";
import API from "src/platform/http/shared-http";
import { IUser } from "src/platform/types/shared-types";
import debugLogger from "src/platform/utils/debugLogger";
import {
  getItem,
  setItem,
  clearAllRushaStorage,
} from "src/platform/utils/storage";

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface AuthResult {
  token: string;
  user?: IUser;
}

/**
 * Centralized Authentication Service
 * Handles all authentication-related operations including token management,
 * login methods, and SSO processing.
 */
class AuthService {
  private oktaAuth: OktaAuth;

  constructor() {
    this.oktaAuth = new OktaAuth(oktaConfig);
  }

  /**
   * Initialize authentication state from storage
   * @returns Object containing stored user and token
   */
  initializeAuth(): { user: IUser | null; token: string | null } {
    const storedUser = getItem(StorageKeys.USER);
    const storedToken = getItem(StorageKeys.AUTH_TOKEN);
    const parsedUser = storedUser ? JSON.parse(storedUser) : null;

    return {
      user: parsedUser,
      token: storedToken,
    };
  }

  /**
   * Store authentication result (token and user).
   * Always clears any previous org session so the user is forced through
   * org selection on every fresh login (two-step auth model).
   */
  storeAuthResult(authResult: AuthResult): void {
    // Clear previous org session — a new login starts without an org context.
    // The user must select an org to obtain a step-2 org-scoped token.
    orgSessionManager.clearSession();
    localStorage.removeItem(StorageKeys.SELECTED_ORGANIZATION);

    if (authResult.token) {
      setItem(StorageKeys.AUTH_TOKEN, authResult.token);
      // Keep OrgSessionManager in sync with the new token
      orgSessionManager.onTokenStored(authResult.token);
    }

    if (authResult.user) {
      setItem(StorageKeys.USER, JSON.stringify(authResult.user));
    }
  }

  /**
   * Direct login with credentials (email/password)
   * @param credentials User login credentials
   * @returns Promise resolving to authentication result
   */
  async login(credentials: LoginCredentials): Promise<AuthResult> {
    try {
      // Use the correct backend base URL for authentication
      const baseUrl = apiBaseUrls.BACKEND;
      const api = new API(baseUrl, AuthEndpoints.LOGIN);
      const response = await api.post<AuthResult>(credentials);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((response as any).error || !(response as any).token) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        throw new Error((response as any).message || "Login failed");
      }
      this.storeAuthResult(response as AuthResult);
      return response as AuthResult;
    } catch (error) {
      debugLogger.error("Login error:", error);
      throw error;
    }
  }

  /**
   * Process Okta authentication result
   * @param authState Okta auth state
   * @returns Authentication result
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  processOktaAuth(authState: any): AuthResult {
    if (!authState.isAuthenticated) {
      throw new Error("Not authenticated with Okta");
    }

    const { idToken, accessToken } = authState.idToken;

    // Create user object from Okta claims
    const user: IUser = {
      email: idToken.claims.email,
      firstName: idToken.claims.given_name,
      lastName: idToken.claims.family_name,
      // Add other properties as needed
    };

    const authResult: AuthResult = {
      token: accessToken,
      user: user,
    };

    // Store auth result
    this.storeAuthResult(authResult);

    // Mark as authenticated with Okta
    setItem(StorageKeys.OKTA_AUTH, "true");

    return authResult;
  }

  /**
   * Process OAuth token received from callback
   * @param token OAuth token
   * @returns Authentication result
   */
  processOAuthToken(token: string): AuthResult {
    const authResult: AuthResult = {
      token: token,
    };

    this.storeAuthResult(authResult);
    return authResult;
  }

  /**
   * Initialize Okta sign-in with redirect
   */
  async loginWithOkta(): Promise<void> {
    try {
      await this.oktaAuth.signInWithRedirect();
    } catch (error) {
      debugLogger.error("Okta login error:", error);
      throw error;
    }
  }

  /**
   * Logout user and clear auth state.
   * Atomically clears all rusha: namespaced keys to prevent stale org/token state.
   * Also clears the OrgSessionManager in-memory state.
   */
  logout(): void {
    orgSessionManager.clearSession();
    clearAllRushaStorage();
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!getItem(StorageKeys.AUTH_TOKEN);
  }

  async signup(userData: {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
  }): Promise<AuthResult> {
    const baseUrl = apiBaseUrls.BACKEND;
    const api = new API(baseUrl, AuthEndpoints.SIGNUP);
    const response = await api.post<AuthResult>(userData);
    if ((response as { error?: string }).error) {
      throw new Error(
        (response as { message?: string }).message || "Sign up failed",
      );
    }
    this.storeAuthResult(response as AuthResult);
    return response as AuthResult;
  }

  async githubLoginLink(): Promise<string> {
    const encodedURL = encodeURIComponent(window.location.origin);
    const api = new API(
      apiBaseUrls.BACKEND,
      `${AuthEndpoints.GITHUB_LOGIN_LINK}?redirect_uri=${encodedURL}`,
    );
    return api.get() as Promise<string>;
  }

  async googleLoginLink(): Promise<string> {
    const encodedURL = encodeURIComponent(window.location.origin);
    const api = new API(
      apiBaseUrls.BACKEND,
      `${AuthEndpoints.GOOGLE_LOGIN_LINK}?redirect_uri=${encodedURL}`,
    );
    return api.get() as Promise<string>;
  }
}

// Export singleton instance
const authService = new AuthService();
export default authService;
